import { useCallback, useMemo, useState } from 'react'

import { useBackupRecordingContext } from '@verbit-ai/verbit-connect-web/backup-recording-provider'
import { useToast } from '@verbit-ai/verbit-ui-library'

import { useIsRecordingStarted, useTranscriptMetadata } from 'src/contexts/Transcript/MetadataContext'
import { useTranscriptClient } from 'src/contexts/Transcript/TranscriptContext'
import { analyticsService } from 'src/services/AnalyticsService'
import { downloadMediaFile, downloadTranscriptFile } from 'src/utils/download'
import { getTranscriptJobId, isZoomAppsEnv } from 'src/utils/env'
import { useFeatureFlag } from 'src/contexts/FeatureFlagContext'
import { getSessionTitle } from 'src/models/TranscriptMetadata'

const isZoomApps = isZoomAppsEnv()
const transcriptJobId = getTranscriptJobId()

export type DownloadOption = {
    labelInList: string
    labelStandalone: string
    action: () => void
}

export const useDownloadOptions = () => {
    const transcriptClient = useTranscriptClient()
    const metadata = useTranscriptMetadata()
    const { backupRecordingsAvailableFor, downloadBackupRecording } = useBackupRecordingContext()
    const [isDownloadInProgress, setIsDownloadInProgress] = useState(false)
    const toast = useToast()

    const canDownloadTranscript = metadata?.downloadable && !isZoomApps
    const downloadTranscript = useCallback(() => {
        if (transcriptClient && metadata) {
            downloadTranscriptFile(transcriptClient, metadata)
            analyticsService.trackDownloadTranscript(metadata.sessionId)
        }
    }, [transcriptClient, metadata])

    const isRemoteMediaDownloadEnabled = useFeatureFlag('s3_media_download')
    const isRecordingStarted = useIsRecordingStarted()
    const canDownloadMedia = isRemoteMediaDownloadEnabled && isRecordingStarted
    const downloadMedia = useCallback(() => {
        if (isDownloadInProgress) {
            return
        }
        setIsDownloadInProgress(true)
        downloadMediaFile(transcriptJobId).then((result) => {
            if (result.type === 'hls') {
                const toastId = toast({
                    type: 'info',
                    title: 'Downloading media...',
                    isClosable: false,
                    duration: 24 * 60 * 60 * 1000,
                })
                result.events.on('progress', (progress) => {
                    toast.update(toastId, {
                        type: 'info',
                        title: `Downloading media: ${Math.round(progress * 100)}%`,
                    })
                    if (progress >= 1) {
                        setIsDownloadInProgress(false)
                        toast.close(toastId)
                    }
                })
                result.events.on('error', () => {
                    setIsDownloadInProgress(false)
                    toast.update(toastId, {
                        type: 'error',
                        title: 'Failed to download media',
                        isClosable: true,
                        duration: 10 * 1000,
                    })
                })
            } else {
                setIsDownloadInProgress(false)
            }
        })
        analyticsService.track('Verbitext 2.0 - user downloaded media', {})
    }, [isDownloadInProgress, toast])

    const canDownloadLocalMedia = !!metadata?.orderId && backupRecordingsAvailableFor.includes(metadata.orderId)
    const downloadLocalMedia = useCallback(() => {
        // analytics are handled by Verbit Connect
        downloadBackupRecording(metadata!.orderId, getSessionTitle(metadata))
    }, [downloadBackupRecording, metadata])

    const downloadOptions = useMemo(() => {
        const options: DownloadOption[] = []

        if (canDownloadTranscript) {
            options.push({
                labelInList: 'Transcript',
                labelStandalone: 'Download transcript',
                action: downloadTranscript,
            })
        }

        if (canDownloadMedia) {
            options.push({
                labelInList: 'Media',
                labelStandalone: 'Download media',
                action: downloadMedia,
            })
        }

        if (canDownloadLocalMedia) {
            options.push({
                labelInList: 'Backup media',
                labelStandalone: 'Download backup media',
                action: downloadLocalMedia,
            })
        }

        return options
    }, [canDownloadLocalMedia, canDownloadMedia, canDownloadTranscript, downloadLocalMedia, downloadMedia, downloadTranscript])

    return downloadOptions
}
